<script lang="ts">
	/**
	 * PresenceGrid — CSS-grid-based replacement for the canvas Radar renderer.
	 *
	 * Rendering responsibilities (no API calls, no zone CRUD):
	 *   - Background 16×16 grid with grid-line overlay
	 *   - Zone cells (from zone.area rect arrays → cell keys)
	 *   - Blind-spot / no-detect cells (dashed, grey)
	 *   - Deadzone overlay (cone when radar position is center only)
	 *   - Sensor node (top-centre or top-left/right for left/right configs)
	 *   - Radar dots from API (radarPoints) and optional simulated points (simulatedPoints)
	 *   - Draw-preview rectangle while the user drags a zone/room rect
	 *
	 * Emits:
	 *   draw     — { zoneRect: [x1,y1,x2,y2], mode } zone-space rect, forwarded from drag
	 *   roomDraw — { zoneRect: [x1,y1,x2,y2], mode, type } same but for room/blind
	 */

	import { onMount, onDestroy, createEventDispatcher } from 'svelte';
	import {
		zoneAreaToCellKeys,
		cellRectToZonePosition,
		radarToCanvasDirect,
		getOrAssignColor,
		ROW_LENGTH,
	} from '../../helpers/radar-helper';
	import { radarPositionManager } from '../../stores/radarPositionStore';
	import Dot from './Dot.svelte';
	import Walker from './Walker.svelte';
	import type { Zone } from '../../Types';
	import type { Device } from 'src/types/Device';

	// ── Props ────────────────────────────────────────────────────────────────
	/** Pre-computed zone cell sets — parent passes this to avoid double computation. */
	export let zoneCells: { zone: Zone; keys: Set<string> }[] = [];
	export let room: [number, number, number, number][] = [];
	export let blind: [number, number, number, number][] = [];
	export let radarPoints: any[] = [];
	export let simulatedPoints: any[] = [];

	export let editable: boolean = false;
	export let editingRoom: boolean = false;
	export let activeRoomEditType: 'blind_spots' | 'room_boundaries' = 'blind_spots';
	export let mode: 'draw' | 'erase' = 'draw';
	export let viewOnly: boolean = false;
	/** When false, furniture items on the canvas are visible but not interactive. */
	export let furnitureInteractive: boolean = true;

	export let radarPosition: 'center' | 'left' | 'right' = 'center';
	export let showCoordinates: boolean = false;
	/** The ID of the master/room zone (e.g. 200). When set, its color is overridden
	 *  to white (#fff) matching the presence-ui-tests convention. */
	export let mainZoneId: number = 200;
	/** RGB for room interior fill; matches main zone on radar. When null, uses a faint white tint. */
	export let roomTintRgb: [number, number, number] | null = null;
	export let device: Device;

	import FurnitureOverlay from './FurnitureOverlay.svelte';
	import { setCurrentDeviceId } from 'src/stores/features/radar/furniture';

	// ── Internal state ───────────────────────────────────────────────────────
	const GRID_SIZE = ROW_LENGTH; // 16
	const dispatch = createEventDispatcher();

	let gridEl: HTMLDivElement;
	let gridSize = 0; // px — set synchronously on mount, then kept current by ResizeObserver
	$: cellSize = gridSize > 0 ? gridSize / GRID_SIZE : 0;

	// Radar dots from the position manager (smoothed + trail)
	let trackedDots = new Map();
	const unsubPositionManager = radarPositionManager.subscribe((dotDataMap) => {
		trackedDots = dotDataMap;
	});

	// Update position manager when radarPoints change — one store flush per frame
	$: if (radarPoints && gridSize) {
		radarPositionManager.batchUpdatePositions(
			radarPoints.map((pt) => {
				const pos = radarToCanvasDirect(pt.x, pt.y, gridSize, gridSize, 8);
				return {
					id: String(pt.id),
					x: pos.x,
					y: pos.y,
					color: pt.color || getOrAssignColor(pt.id),
				};
			}),
		);
		radarPositionManager.markInactive(radarPoints.map((pt) => String(pt.id)));
	}

	// ── Derived: cell sets for rendering ─────────────────────────────────────
	$: blindCells = blind?.length
		? zoneAreaToCellKeys(blind as [number, number, number, number][])
		: new Set<string>();

	$: roomCells = room?.length
		? zoneAreaToCellKeys(room as [number, number, number, number][])
		: new Set<string>();

	// OOB: cells outside the defined room perimeter, styled as grey walls matching
	// the prototype's room-boundary type (#C1C1C1). Empty when no room is defined.
	$: outOfBoundaryCells = (() => {
		if (roomCells.size === 0) return new Set<string>();
		const oob = new Set<string>();
		for (let r = 0; r < GRID_SIZE; r++) {
			for (let c = 0; c < GRID_SIZE; c++) {
				if (!roomCells.has(`${r},${c}`)) oob.add(`${r},${c}`);
			}
		}
		return oob;
	})();

	// ── Blind-spot icon anchors (one eye-off per connected component) ─────────
	// BFS flood-fill to find contiguous regions, then place the icon at the
	// fractional centroid of each component — works correctly for even-sized
	// squares (e.g. 4×4) where a cell-based deepest-point gives an off-center result.
	$: blindIconPositions = computeBlindIconPositions(blindCells);

	function computeBlindIconPositions(cells: Set<string>): { px: number; py: number }[] {
		if (cells.size === 0) return [];
		const unvisited = new Set(cells);
		const anchors: { px: number; py: number }[] = [];

		while (unvisited.size > 0) {
			const seed = unvisited.values().next().value as string;
			const queue: string[] = [seed];
			const component: string[] = [];
			unvisited.delete(seed);

			while (queue.length > 0) {
				const cur = queue.shift()!;
				component.push(cur);
				const [r, c] = cur.split(',').map(Number);
				for (const [dr, dc] of [
					[-1, 0],
					[1, 0],
					[0, -1],
					[0, 1],
				] as const) {
					const nk = `${r + dr},${c + dc}`;
					if (unvisited.has(nk)) {
						unvisited.delete(nk);
						queue.push(nk);
					}
				}
			}

			// Centroid of the component cells — each cell contributes its centre (r+0.5, c+0.5)
			let sumR = 0,
				sumC = 0;
			for (const key of component) {
				const [r, c] = key.split(',').map(Number);
				sumR += r + 0.5;
				sumC += c + 0.5;
			}
			anchors.push({ px: sumC / component.length, py: sumR / component.length });
		}
		return anchors;
	}

	// ── Draw interaction ─────────────────────────────────────────────────────
	let drawing = false;
	let startCell: { row: number; col: number } | null = null;
	let endCell: { row: number; col: number } | null = null;

	function getCell(event: PointerEvent): { row: number; col: number } | null {
		const rect = gridEl.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;
		const col = Math.floor(x / cellSize);
		const row = Math.floor(y / cellSize);
		if (row >= 0 && row < GRID_SIZE && col >= 0 && col < GRID_SIZE) {
			return { row, col };
		}
		return null;
	}

	function onPointerDown(e: PointerEvent) {
		if (viewOnly || (!editable && !editingRoom)) return;
		if (e.button !== 0) return;
		const cell = getCell(e);
		if (!cell) return;
		drawing = true;
		startCell = cell;
		endCell = cell;
		gridEl.setPointerCapture(e.pointerId);
	}

	function onPointerMove(e: PointerEvent) {
		if (!drawing || !startCell) return;
		const cell = getCell(e);
		if (cell) endCell = cell;
	}

	function onPointerUp(e: PointerEvent) {
		if (!drawing || !startCell || !endCell) {
			drawing = false;
			return;
		}
		drawing = false;
		if (gridEl.hasPointerCapture(e.pointerId)) {
			gridEl.releasePointerCapture(e.pointerId);
		}

		const r1 = Math.min(startCell.row, endCell.row);
		const c1 = Math.min(startCell.col, endCell.col);
		const r2 = Math.max(startCell.row, endCell.row) + 1;
		const c2 = Math.max(startCell.col, endCell.col) + 1;

		const cellRect: [number, number, number, number] = [r1, c1, r2, c2];
		const zoneRect = cellRectToZonePosition(cellRect);

		if (editingRoom) {
			dispatch('roomDraw', { zoneRect, mode, type: activeRoomEditType });
		} else {
			dispatch('draw', { zoneRect, mode });
		}

		startCell = null;
		endCell = null;
	}

	// Preview rect in pixel coords
	$: previewRect =
		drawing && startCell && endCell
			? {
					x: Math.min(startCell.col, endCell.col) * cellSize,
					y: Math.min(startCell.row, endCell.row) * cellSize,
					w: (Math.abs(endCell.col - startCell.col) + 1) * cellSize,
					h: (Math.abs(endCell.row - startCell.row) + 1) * cellSize,
				}
			: null;

	// ── Sensor node sizing (scales with gridSize like walker indicators) ──────
	$: sensorSize = Math.max(24, (gridSize / 16) * 1.5);
	$: sensorIconSize = Math.max(14, (gridSize / 16) * 0.9);

	// Sensor anchor: inset from grid edge; circle center uses translate(-50%, -50%).
	const SENSOR_EDGE_INSET_PX = 0;

	$: sensorStyle = (() => {
		const r = sensorSize / 2;
		const cxLeft = `${SENSOR_EDGE_INSET_PX + r}px`;
		const cxRight = `calc(100% - ${SENSOR_EDGE_INSET_PX + r}px)`;
		const cy = `${SENSOR_EDGE_INSET_PX}px`;
		switch (radarPosition) {
			case 'left':
				return `left: ${cxLeft}; top: ${cy}; transform: translate(-50%, -50%);`;
			case 'right':
				return `left: ${cxRight}; top: ${cy}; transform: translate(-50%, -50%);`;
			default:
				return `left: 50%; top: ${cy}; transform: translate(-50%, -50%);`;
		}
	})();

	// ── Deadzone clip-path (center mount only) ──────────────────────────────
	// Dark wedge where the radar does not cover. Shown only for **center** — wall/side
	// mounts (left/right) omit the cone entirely.
	// Center (140° FOV): half-angle ≈ 70°, wall depth ≈ 18%
	const DEADZONE_CLIP_CENTER = 'polygon(50% 0%, 100% 18%, 100% 0%, 0% 0%, 0% 18%)';

	// ── Device / furniture store sync ─────────────────────────────────────────
	// Guard: only reload furniture when the device ID actually changes.
	// Using a plain reactive statement on `device?.id` causes re-fires on every
	// parent re-render (new object reference), which would reset the selection.
	let _loadedDeviceId: string | null = null;
	$: if (device?.id && device.id !== _loadedDeviceId) {
		_loadedDeviceId = device.id;
		setCurrentDeviceId(device.id);
	}

	// ── Lifecycle ─────────────────────────────────────────────────────────────
	let resizeObserver: ResizeObserver;

	onMount(() => {
		// Read the real size synchronously first — eliminates the flash caused by
		// the ResizeObserver callback being async on first paint.
		gridSize = gridEl.getBoundingClientRect().width;

		resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				gridSize = entry.contentRect.width;
			}
		});
		resizeObserver.observe(gridEl);
	});

	onDestroy(() => {
		resizeObserver?.disconnect();
		unsubPositionManager();
		radarPositionManager.clear();
	});

	// ── Helpers for cell styling (edge detection) ─────────────────────────────
	function neighborKey(row: number, col: number) {
		return `${row},${col}`;
	}

	function cellStyle(
		row: number,
		col: number,
		color: string,
		alpha: number,
		cells: Set<string>,
		suppressBorders = false,
		_cellSize = cellSize,
	): string {
		const top = cells.has(neighborKey(row - 1, col));
		const right = cells.has(neighborKey(row, col + 1));
		const bottom = cells.has(neighborKey(row + 1, col));
		const left = cells.has(neighborKey(row, col - 1));

		const radius = [
			!top && !left ? '4px' : '0',
			!top && !right ? '4px' : '0',
			!bottom && !right ? (row === GRID_SIZE - 1 && col === GRID_SIZE - 1 ? '11px' : '4px') : '0',
			!bottom && !left ? (row === GRID_SIZE - 1 && col === 0 ? '11px' : '4px') : '0',
		].join(' ');

		// For the main zone (room background) suppress borders and shadows so it doesn't
		// draw a coloured outline at the room boundary edge.
		if (suppressBorders) {
			return [
				`left:${col * _cellSize}px`,
				`top:${row * _cellSize}px`,
				`width:${_cellSize}px`,
				`height:${_cellSize}px`,
				`background-color:rgba(${color},${alpha * 0.2})`,
				`border-radius:${radius}`,
			].join(';');
		}

		const edgeShadows: string[] = [];
		if (!right && col < GRID_SIZE - 1) edgeShadows.push(`1px 0 0 0 rgba(${color},${alpha})`);
		if (!bottom && row < GRID_SIZE - 1) edgeShadows.push(`0 1px 0 0 rgba(${color},${alpha})`);

		const showTop = !top;
		const showLeft = !left;

		return [
			`left:${col * _cellSize}px`,
			`top:${row * _cellSize}px`,
			`width:${_cellSize}px`,
			`height:${_cellSize}px`,
			`background-color:rgba(${color},${alpha * 0.2})`,
			`border-top:${showTop ? `1px solid rgba(${color},${alpha})` : 'none'}`,
			`border-left:${showLeft ? `1px solid rgba(${color},${alpha})` : 'none'}`,
			`border-right:${!right && col === GRID_SIZE - 1 ? `1px solid rgba(${color},${alpha})` : 'none'}`,
			`border-bottom:${!bottom && row === GRID_SIZE - 1 ? `1px solid rgba(${color},${alpha})` : 'none'}`,
			`border-radius:${radius}`,
			edgeShadows.length ? `box-shadow:${edgeShadows.join(',')}` : '',
		]
			.filter(Boolean)
			.join(';');
	}

	// OOB cells styled as grey walls (#C1C1C1), matching the prototype room-boundary type.
	// Borders render only on exposed edges (facing the room); adjacent OOB cells share no border.
	function oobCellStyle(row: number, col: number, cells: Set<string>, _cellSize = cellSize): string {
		const color = '193,193,193';
		const top = cells.has(`${row - 1},${col}`);
		const right = cells.has(`${row},${col + 1}`);
		const bottom = cells.has(`${row + 1},${col}`);
		const left = cells.has(`${row},${col - 1}`);
		const radius = [
			!top && !left ? '4px' : '0',
			!top && !right ? '4px' : '0',
			!bottom && !right ? '4px' : '0',
			!bottom && !left ? '4px' : '0',
		].join(' ');
		const edgeShadows: string[] = [];
		if (!right && col < GRID_SIZE - 1) edgeShadows.push(`1px 0 0 0 rgba(${color},1)`);
		if (!bottom && row < GRID_SIZE - 1) edgeShadows.push(`0 1px 0 0 rgba(${color},1)`);
		return [
			`left:${col * _cellSize}px`,
			`top:${row * _cellSize}px`,
			`width:${_cellSize}px`,
			`height:${_cellSize}px`,
			`background-color:rgba(${color},0.25)`,
			!top ? `border-top:1px solid rgba(${color},1)` : '',
			!left ? `border-left:1px solid rgba(${color},1)` : '',
			!right && col === GRID_SIZE - 1 ? `border-right:1px solid rgba(${color},1)` : '',
			!bottom && row === GRID_SIZE - 1 ? `border-bottom:1px solid rgba(${color},1)` : '',
			`border-radius:${radius}`,
			edgeShadows.length ? `box-shadow:${edgeShadows.join(',')}` : '',
		]
			.filter(Boolean)
			.join(';');
	}

	function blindCellStyle(row: number, col: number, cells: Set<string>, _cellSize = cellSize): string {
		const top = cells.has(neighborKey(row - 1, col));
		const right = cells.has(neighborKey(row, col + 1));
		const bottom = cells.has(neighborKey(row + 1, col));
		const left = cells.has(neighborKey(row, col - 1));
		const showTop = !top;
		const showRight = !right;
		const showBottom = !bottom;
		const showLeft = !left;
		// Round only the outer corners of the connected blind-spot region.
		// A corner is "outer" when neither of its two adjacent sides has a cell neighbour.
		const rTL = !top && !left ? '4px' : '0';
		const rTR = !top && !right ? '4px' : '0';
		const rBR = !bottom && !right ? '4px' : '0';
		const rBL = !bottom && !left ? '4px' : '0';
		return [
			`left:${col * _cellSize}px`,
			`top:${row * _cellSize}px`,
			`width:${_cellSize}px`,
			`height:${_cellSize}px`,
			`border-top-width:${showTop ? '1px' : '0'}`,
			`border-right-width:${showRight ? '1px' : '0'}`,
			`border-bottom-width:${showBottom ? '1px' : '0'}`,
			`border-left-width:${showLeft ? '1px' : '0'}`,
			`border-radius:${rTL} ${rTR} ${rBR} ${rBL}`,
		].join(';');
	}
</script>

<!-- ─── Root wrapper ──────────────────────────────────────────────────────── -->
<div class="presence-grid-wrap" class:editable={editable || editingRoom}>
	<!-- Grid area: layers (dots render in a sibling overlay so they sit above the sensor shadow) -->
	<div
		bind:this={gridEl}
		class="grid-area"
		role="img"
		aria-label="Presence detection grid"
		on:pointerdown={onPointerDown}
		on:pointermove={onPointerMove}
		on:pointerup={onPointerUp}
		on:pointercancel={() => {
			drawing = false;
			startCell = null;
			endCell = null;
		}}
	>
		<!-- 1. Grid lines background (CSS only, no extra DOM) -->
		<div class="grid-bg" aria-hidden="true"></div>

		<!-- 2. Room boundaries (tint matches main zone colour, same treatment as zone fill) -->
		{#if roomCells.size > 0}
			<div class="room-layer" aria-hidden="true">
				{#each [...roomCells] as key (key)}
					{@const [row, col] = key.split(',').map(Number)}
					{@const roomColorStr = roomTintRgb
						? `${roomTintRgb[0]},${roomTintRgb[1]},${roomTintRgb[2]}`
						: '255,255,255'}
					{@const roomColorAlpha = roomTintRgb ? 1 : 0.2}
					<span
						class="room-cell"
						style={cellStyle(
							row,
							col,
							roomColorStr,
							roomColorAlpha,
							roomCells,
							true,
							cellSize,
						)}
					></span>
				{/each}
			</div>
		{/if}

		<!-- 3. Deadzone overlay (center mount only — no cone for left/right wall positions) -->
		{#if radarPosition === 'center'}
			<div class="deadzone" style="clip-path: {DEADZONE_CLIP_CENTER}" aria-hidden="true"></div>
		{/if}

		<!-- 4. OOB layer — grey walls for cells outside the defined room boundary -->
		{#if outOfBoundaryCells.size > 0}
			<div class="oob-layer" aria-hidden="true">
				{#each [...outOfBoundaryCells] as key (key)}
					{@const [row, col] = key.split(',').map(Number)}
					<span class="oob-cell" style={oobCellStyle(row, col, outOfBoundaryCells, cellSize)}
					></span>
				{/each}
			</div>
		{/if}

		<!-- 5. Zone layers (one per zone, in ID order) -->
		<div class="zones-layer" aria-hidden="true">
			{#each zoneCells as { zone, keys } (zone.id)}
				{#if keys.size > 0}
					{@const isMain = mainZoneId !== null && zone.id === mainZoneId}
					{@const [cr, cg, cb] = zone.color}
					{@const colorStr = `${cr},${cg},${cb}`}
					{#each [...keys] as key (key)}
						{@const [row, col] = key.split(',').map(Number)}
						<span
							class="zone-cell"
							style={cellStyle(row, col, colorStr, 1, keys, isMain, cellSize)}
						></span>
					{/each}
				{/if}
			{/each}
		</div>

		<!-- 6. Blind-spot / no-detect layer -->
		{#if blindCells.size > 0}
			<div class="no-detect-layer" aria-hidden="true">
				{#each [...blindCells] as key (key)}
					{@const [row, col] = key.split(',').map(Number)}
					<span class="no-detect-cell" style={blindCellStyle(row, col, blindCells, cellSize)}
					></span>
				{/each}
				{#each blindIconPositions as pos (pos.px + ',' + pos.py)}
					<i
						class="no-detect-icon fas fa-eye-slash"
						style="left:{pos.px * cellSize}px;top:{pos.py * cellSize}px;font-size:{Math.max(
							12,
							cellSize * 1.1,
						)}px"
						aria-hidden="true"
					></i>
				{/each}
			</div>
		{/if}

		<!-- 7. Dev coordinate overlay -->
		{#if showCoordinates}
			<div class="coord-layer" aria-hidden="true">
				{#each Array(GRID_SIZE) as _, row}
					{#each Array(GRID_SIZE) as _, col}
						<span
							class="coord-label"
							style="left:{col * cellSize}px;top:{row *
								cellSize}px;width:{cellSize}px;height:{cellSize}px"
						>
							{row},{col}
						</span>
					{/each}
				{/each}
			</div>
		{/if}

		<!-- 8. Draw preview -->
		{#if previewRect}
			<div
				class="draw-preview"
				class:erase={mode === 'erase'}
				class:room-boundary-erase={editingRoom &&
					activeRoomEditType === 'room_boundaries' &&
					mode === 'erase'}
				style="left:{previewRect.x}px;top:{previewRect.y}px;width:{previewRect.w}px;height:{previewRect.h}px"
				aria-hidden="true"
			></div>
		{/if}

		<!-- 9. Furniture overlay (fraction-based, pointer-capture interactions) -->
		<FurnitureOverlay {gridSize} interactive={!viewOnly && furnitureInteractive} />
	</div>

	<!-- Sensor node — above floor layers, below dots so shadow does not cover people icons -->
	<div
		class="sensor-node"
		style="{sensorStyle};width:{sensorSize}px;height:{sensorSize}px"
		aria-hidden="true"
	>
		<i class="sensor-icon fas fa-radar" style:font-size="{sensorIconSize}px" aria-hidden="true"></i>
	</div>

	<!-- 10. Radar dots + walkers — top stacking so presence renders over sensor drop shadow -->
	<div class="dots-layer">
		{#each [...trackedDots.values()] as dotData (dotData.id)}
			<Dot
				x={dotData.currentPosition.x}
				y={dotData.currentPosition.y}
				color={dotData.color}
				{dotData}
				{gridSize}
			/>
		{/each}
		{#each simulatedPoints as pt (pt.id)}
			<Walker
				color={pt.energetic ? '#FF7200' : pt.color || '#7C5BFF'}
				energetic={pt.energetic || false}
				{gridSize}
			/>
		{/each}
	</div>
</div>

<style>
	/* ── Root ────────────────────────────────────────────────────────────────── */
	.presence-grid-wrap {
		width: 100%;
		aspect-ratio: 1 / 1;
		position: relative;
		background: var(--color-surface-secondary, #303235);
		border-radius: 10px;
		overflow: visible; /* allow sensor node to bleed above */
		display: flex;
		align-items: flex-end;
		justify-content: center;
	}

	/* ── Sensor node ─────────────────────────────────────────────────────────── */
	.sensor-node {
		position: absolute;
		width: var(--sensor-size, 24px);
		height: var(--sensor-size, 24px);
		background:
			radial-gradient(80% 80% at 50% 20%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 100%), #0088ff;
		box-shadow:
			0 4px 4px rgba(0, 0, 0, 0.25),
			inset 0 -1px 0 rgba(255, 255, 255, 0.25);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #fff;
		z-index: 8;
		pointer-events: none;
	}

	.sensor-icon {
		font-size: var(--sensor-icon-size, 14px);
		line-height: 1;
	}

	/* ── Grid area ───────────────────────────────────────────────────────────── */
	.grid-area {
		width: 100%;
		height: 100%;
		position: relative;
		z-index: 1;
		overflow: hidden;
		user-select: none;
		touch-action: none;
		cursor: default;
		border-radius: 10px;
		/* OOB areas (outside room boundary) show as this dark panel background,
		   matching the prototype approach (no explicit OOB overlay needed). */
		background: #1e2022;
	}

	.presence-grid-wrap.editable .grid-area {
		cursor: crosshair;
	}

	/* ── 1. Grid background ──────────────────────────────────────────────────── */
	.grid-bg {
		position: absolute;
		inset: 0;
		z-index: 0;
		pointer-events: none;
		/* Lines sit at the LEFT/TOP of each cell tile.
		   Offset by one cell so the first line falls between col 0 and col 1.
		   This gives 15 internal lines in each direction with no border at the outer edges. */
		background-image:
			linear-gradient(
				to right,
				rgba(255, 255, 255, 0.1) 0,
				rgba(255, 255, 255, 0.1) 1px,
				transparent 1px
			),
			linear-gradient(
				to bottom,
				rgba(255, 255, 255, 0.1) 0,
				rgba(255, 255, 255, 0.1) 1px,
				transparent 1px
			);
		background-size: calc(100% / 16) calc(100% / 16);
		background-position: calc(100% / 16) calc(100% / 16);
	}

	/* ── 2. Room layer ───────────────────────────────────────────────────────── */
	.room-layer {
		position: absolute;
		inset: 0;
		z-index: 1;
		pointer-events: none;
	}

	.room-cell {
		position: absolute;
		box-sizing: border-box;
	}

	/* ── 3. Deadzone ─────────────────────────────────────────────────────────── */
	.deadzone {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.28);
		z-index: 2;
		pointer-events: none;
	}

	/* ── 4. OOB (room boundary walls) ───────────────────────────────────────── */
	.oob-layer {
		position: absolute;
		inset: 0;
		z-index: 4;
		pointer-events: none;
	}

	.oob-cell {
		position: absolute;
		box-sizing: border-box;
	}

	/* ── 5. Zones ────────────────────────────────────────────────────────────── */
	.zones-layer {
		position: absolute;
		inset: 0;
		z-index: 3;
		pointer-events: none;
	}

	.zone-cell {
		position: absolute;
		border: 1px solid transparent;
		box-sizing: border-box;
	}

	/* ── 6. No-detect / blind spots ──────────────────────────────────────────── */
	.no-detect-layer {
		position: absolute;
		inset: 0;
		z-index: 5;
		pointer-events: none;
	}

	.no-detect-cell {
		position: absolute;
		background: rgba(93, 93, 93, 0.7);
		border-style: dashed;
		border-color: #c6c6c6;
		box-sizing: border-box;
		backdrop-filter: blur(2px);
		/* border-radius is set per-cell inline via blindCellStyle() */
	}

	.no-detect-icon {
		position: absolute;
		transform: translate(-50%, -50%);
		color: rgba(198, 198, 198, 0.75);
		pointer-events: none;
		z-index: 5;
		line-height: 1;
	}

	/* ── 7. Dev coordinate overlay ───────────────────────────────────────────── */
	.coord-layer {
		position: absolute;
		inset: 0;
		z-index: 6;
		pointer-events: none;
	}

	.coord-label {
		position: absolute;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 8px;
		color: rgba(255, 255, 255, 0.45);
		font-family: monospace;
		pointer-events: none;
	}

	/* ── 8. Draw preview ─────────────────────────────────────────────────────── */
	.draw-preview {
		position: absolute;
		border: 1px dashed rgba(255, 255, 255, 0.9);
		background: rgba(255, 255, 255, 0.1);
		border-radius: 4px;
		pointer-events: none;
		z-index: 9;
	}

	.draw-preview.erase {
		border-color: rgba(255, 114, 0, 0.95);
		background: rgba(255, 114, 0, 0.14);
	}

	.draw-preview.erase.room-boundary-erase {
		background: rgba(180, 70, 0, 0.42);
		border-color: rgba(255, 160, 80, 0.98);
	}

	/* ── 10. Dots (stacked above .sensor-node so drop-shadow does not cover people) ─ */
	.dots-layer {
		position: absolute;
		inset: 0;
		pointer-events: none;
		z-index: 10;
		border-radius: 10px;
	}
</style>
